import type { KeycloakConfig, KeycloakTokenParsed } from '@/shared/types/keycloak.type'
import Keycloak from 'keycloak-js'
import { computed, readonly, ref } from 'vue'
import { captureLocationForRestore } from '@/shared/utils/postLogoutRedirect'

// Realm timeouts — verified against production 2026-05-12:
//   accessTokenLifespan:    300s   (5 min)
//   ssoSessionIdleTimeout:  1800s  (30 min) — session dies after this much inactivity
//   ssoSessionMaxLifespan:  36000s (10 h)   — hard cap, regardless of activity
// HEARTBEAT_INTERVAL_MS below (5 min) has a 6× margin under the idle timeout.
// If realm values change, re-verify the heartbeat margin.

const keycloakConfig: KeycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID
}

const REFRESH_TICK_MS = 60_000
const REFRESH_MIN_VALIDITY_S = 70
const REFRESH_RETRY_BACKOFF_MS = [2_000, 5_000] as const
const FLUSH_TIMEOUT_MS = 3_000

// Wake-from-sleep detector. After a laptop suspend, neither visibilitychange
// nor focus reliably fire on resume (visibility/focus didn't change during
// sleep). The watchdog setTimeout queues but its firing order on resume is
// browser-specific, so the user could click around for up to ~60s on a dead
// session before the next setInterval heartbeat catches it. A short Date.now()
// gap-detector catches resume regardless of which other events fired.
const WAKE_CHECK_INTERVAL_MS = 1_000
const WAKE_GAP_THRESHOLD_MS = 3_000

// Default until the backend fetch (fetchSessionTimeouts) reports the realm's
// actual ssoSessionMaxLifespan. To test against a different value, change the
// realm setting in Keycloak admin — the frontend picks it up on next reload.
const DEFAULT_SSO_MAX_LIFESPAN_MS = 10 * 60 * 60 * 1000

// Heartbeat: force a token refresh on this cadence while the tab is focused.
// Each forced refresh resets the SSO idle timer on Keycloak and fetches a new
// refresh token (which updates the locally cached exp). Skipped when the tab
// is hidden so abandoned tabs still time out naturally. Must be well under
// the realm's ssoSessionIdleTimeout.
//
// Dev override: localStorage.setItem('debug-heartbeat-s', '60') for testing
// against a low ssoSessionIdleTimeout (e.g. 3 min) without waiting for the
// production cadence.
const HEARTBEAT_INTERVAL_MS = ((): number => {
  const DEFAULT = 5 * 60 * 1000
  if (!import.meta.env.DEV) return DEFAULT
  const raw = localStorage.getItem('debug-heartbeat-s')
  const seconds = raw === null ? NaN : Number(raw)
  if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT
  console.warn(`[useKeycloak] dev heartbeat override active: ${seconds}s`)
  return seconds * 1000
})()

type FlushHandler = () => void | Promise<void>

const keycloak = new Keycloak(keycloakConfig)
const isAuthenticated = ref(false)
const isInitialized = ref(false)
const isLoggingOut = ref(false)
const isVerifyingSession = ref(false)
const userProfile = ref<KeycloakTokenParsed | null>(null)
const error = ref<string | null>(null)
const refreshTokenExpiresAt = ref<number | null>(null)
const authStartedAt = ref<number | null>(null)
const ssoMaxLifespanMs = ref<number>(DEFAULT_SSO_MAX_LIFESPAN_MS)
let refreshTokenInterval: ReturnType<typeof setInterval> | null = null
let refreshDeadlineTimer: ReturnType<typeof setTimeout> | null = null
let wakeDetectorInterval: ReturnType<typeof setInterval> | null = null
let lastWakeCheckAt = 0
let visibilityListener: (() => void) | null = null
let ticksSinceForceRefresh = 0
const flushHandlers = new Set<FlushHandler>()

// Number of REFRESH_TICK_MS ticks between forced refreshes. Using a counter
// instead of wall-clock comparison avoids setInterval/Date.now() jitter — a
// tick that fires ~1ms late made the previous wall-clock check skip every
// other heartbeat.
const HEARTBEAT_TICKS = Math.max(1, Math.round(HEARTBEAT_INTERVAL_MS / REFRESH_TICK_MS))

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

const isRefreshTokenExpiredError = (err: unknown): boolean => {
  if (!err) return false
  const message = typeof err === 'string' ? err : (err as { message?: string })?.message
  if (!message) return false
  return /refresh token|session/i.test(message) && /expired|invalid|not active/i.test(message)
}

const clearRefreshDeadlineTimer = (): void => {
  if (refreshDeadlineTimer) {
    clearTimeout(refreshDeadlineTimer)
    refreshDeadlineTimer = null
  }
}

// Fire handleTerminalRefreshFailure at the exact moment the refresh token
// expires, instead of waiting up to ~60s for the next refresh tick to notice.
const scheduleRefreshDeadline = (): void => {
  clearRefreshDeadlineTimer()
  const exp = refreshTokenExpiresAt.value
  if (exp === null) return
  const delay = Math.max(0, exp - Date.now())
  refreshDeadlineTimer = setTimeout(() => {
    refreshDeadlineTimer = null
    void handleTerminalRefreshFailure(new Error('refresh token expired'))
  }, delay)
}

const updateRefreshTokenExpiry = (): void => {
  const exp = (keycloak.refreshTokenParsed as { exp?: number } | undefined)?.exp
  refreshTokenExpiresAt.value = typeof exp === 'number' ? exp * 1000 : null
  scheduleRefreshDeadline()
}

// Pulls the realm's actual ssoSessionMaxLifespan from the backend
// (/api/v1/auth/session-timeouts). Updates ssoMaxLifespanMs reactively so the
// dialog's "Daily session limit reached" detection uses the realm value
// instead of the hardcoded default. Silent on failure — the default (10 h)
// stays in effect and worst case is the modal misjudges by a few minutes
// near the cap.
const fetchSessionTimeouts = async (baseApiUrl: string): Promise<void> => {
  try {
    const response = await fetch(`${baseApiUrl}/auth/session-timeouts`, {
      headers: { Accept: 'application/json' }
    })
    if (!response.ok) return
    const data = (await response.json()) as
      | { ssoSessionMaxLifespan?: number }
      | { unavailable: true }
    if ('unavailable' in data) return
    if (typeof data.ssoSessionMaxLifespan === 'number' && data.ssoSessionMaxLifespan > 0) {
      ssoMaxLifespanMs.value = data.ssoSessionMaxLifespan * 1000
    }
  }
  catch {
    // Network/JSON failure — keep default.
  }
}

// Records when the SSO session originally started, surviving token refreshes.
// Prefers the OIDC `auth_time` claim (when present) so a page reload doesn't
// reset our local view of when the max started ticking. Falls back to
// Date.now() on the first call when the claim is absent.
const updateAuthStartedAt = (): void => {
  const authTime = (keycloak.tokenParsed as { auth_time?: number } | undefined)?.auth_time
  if (typeof authTime === 'number' && authTime > 0) {
    authStartedAt.value = authTime * 1000
  }
  else if (authStartedAt.value === null) {
    authStartedAt.value = Date.now()
  }
}

// A sign-out or redirect gives the saves a moment and goes; a write that names what was saved waits for it
const runFlushHandlers = async ({ untilDone = false } = {}): Promise<void> => {
  if (flushHandlers.size === 0) return
  const work = Promise.allSettled(
    Array.from(flushHandlers, (handler) => {
      try {
        return Promise.resolve(handler())
      }
      catch (err) {
        return Promise.reject(err as Error)
      }
    })
  )
  if (untilDone) await work
  else await Promise.race([work, sleep(FLUSH_TIMEOUT_MS)])
}

const clearAuthState = (reason: string): void => {
  isAuthenticated.value = false
  userProfile.value = null
  refreshTokenExpiresAt.value = null
  authStartedAt.value = null
  error.value = reason

  if (refreshTokenInterval) {
    clearInterval(refreshTokenInterval)
    refreshTokenInterval = null
  }
  clearRefreshDeadlineTimer()
}

const forceLogoutRedirect = async (): Promise<void> => {
  try {
    await keycloak.logout({ redirectUri: window.location.origin })
  }
  catch (err) {
    console.error('Logout redirect failed, forcing navigation:', err)
    window.location.assign(window.location.origin)
  }
}

let terminalFailureInFlight = false

const handleTerminalRefreshFailure = async (lastError: unknown): Promise<void> => {
  if (terminalFailureInFlight) return
  terminalFailureInFlight = true
  isLoggingOut.value = true
  console.error('Token refresh exhausted retries:', lastError)
  // Involuntary logout — remember where the user was so we can send them back
  // after they re-authenticate. (Manual logout deliberately skips this.)
  captureLocationForRestore()
  try {
    await runFlushHandlers()
  }
  catch (flushErr) {
    console.error('Flush before logout failed:', flushErr)
  }
  clearAuthState('Session expired. Please log in again.')
  await forceLogoutRedirect()
}

const tryRefreshOnce = async (minValidity: number): Promise<boolean> => {
  const refreshed = await keycloak.updateToken(minValidity)
  if (keycloak.tokenParsed) {
    userProfile.value = keycloak.tokenParsed as KeycloakTokenParsed
  }
  updateRefreshTokenExpiry()
  updateAuthStartedAt()
  return refreshed
}

const refreshWithRetry = async (minValidity: number): Promise<boolean> => {
  let lastError: unknown
  for (let attempt = 0; attempt <= REFRESH_RETRY_BACKOFF_MS.length; attempt++) {
    try {
      return await tryRefreshOnce(minValidity)
    }
    catch (err) {
      lastError = err
      if (isRefreshTokenExpiredError(err)) {
        // No point retrying — Keycloak says the session is gone.
        break
      }
      const backoff = REFRESH_RETRY_BACKOFF_MS[attempt]
      if (backoff === undefined) break
      console.warn(`Token refresh attempt ${attempt + 1} failed, retrying in ${backoff}ms`, err)
      await sleep(backoff)
    }
  }
  throw lastError
}

const isTabVisible = (): boolean => {
  if (typeof document === 'undefined') return true
  return document.visibilityState === 'visible'
}

// Runs the periodic token refresh. When `force` is true (heartbeat tick or
// tab-focus event), bypass the min-validity check and request a new refresh
// token from Keycloak — that's what keeps the SSO idle timer alive and the
// locally cached refresh_exp moving forward. When false, only refresh if the
// access token is near expiry (cheap local check, no HTTP).
// When `showFeedback` is true, the dialog shows a "Verifying session…" state
// for the duration of the refresh — used for wake-from-sleep and tab-return so
// the user sees something is happening instead of clicking into a dead UI.
const tickRefresh = async (force: boolean, showFeedback = false): Promise<void> => {
  if (force) ticksSinceForceRefresh = 0
  if (showFeedback) isVerifyingSession.value = true
  try {
    await refreshWithRetry(force ? -1 : REFRESH_MIN_VALIDITY_S)
  }
  catch (err) {
    await handleTerminalRefreshFailure(err)
  }
  finally {
    if (showFeedback) isVerifyingSession.value = false
  }
}

const setupTokenRefresh = (): void => {
  cleanupTokenRefresh()
  updateRefreshTokenExpiry()
  ticksSinceForceRefresh = 0
  lastWakeCheckAt = Date.now()

  refreshTokenInterval = setInterval(() => {
    // Skip while the tab is hidden — setInterval is throttled by the browser
    // anyway, and we don't want an abandoned background tab to keep the SSO
    // session alive forever. The visibilitychange listener catches the moment
    // the user returns.
    if (!isTabVisible()) return
    ticksSinceForceRefresh++
    const shouldHeartbeat = ticksSinceForceRefresh >= HEARTBEAT_TICKS
    void tickRefresh(shouldHeartbeat)
  }, REFRESH_TICK_MS)

  // Wake-from-sleep detector: when this ticker fires significantly later than
  // expected, the JS engine was paused (laptop sleep, OS suspend, etc.).
  // Force a refresh check immediately so the user can't interact with a dead
  // session in the window before the next heartbeat tick.
  wakeDetectorInterval = setInterval(() => {
    const now = Date.now()
    const gap = now - lastWakeCheckAt
    lastWakeCheckAt = now
    if (gap > WAKE_GAP_THRESHOLD_MS) {
      console.warn(`[useKeycloak] detected ${gap}ms gap, likely wake-from-sleep — forcing refresh`)
      void tickRefresh(true, true)
    }
  }, WAKE_CHECK_INTERVAL_MS)

  visibilityListener = () => {
    if (!isTabVisible()) return
    // Force a refresh on return: confirms the session is still alive, or
    // surfaces that it died while the tab was backgrounded.
    void tickRefresh(true, true)
  }
  document.addEventListener('visibilitychange', visibilityListener)
}

const cleanupTokenRefresh = (): void => {
  if (refreshTokenInterval) {
    clearInterval(refreshTokenInterval)
    refreshTokenInterval = null
  }
  if (wakeDetectorInterval) {
    clearInterval(wakeDetectorInterval)
    wakeDetectorInterval = null
  }
  if (visibilityListener && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityListener)
    visibilityListener = null
  }
  clearRefreshDeadlineTimer()
}

export const useKeycloak = () => {
  const initKeycloak = async (options?: { onLoad?: 'login-required' | 'check-sso' }): Promise<boolean> => {
    try {
      const authenticated = await keycloak.init({
        // Default to 'login-required' because the app uses
        // Cross-Origin-Embedder-Policy for SharedArrayBuffer support, which
        // blocks Keycloak's 3rd party cookie check iframe.
        // Public routes (e.g. accept-invitation) use 'check-sso' to allow
        // unauthenticated access.
        onLoad: options?.onLoad ?? 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false
      })

      isAuthenticated.value = authenticated
      isInitialized.value = true

      if (authenticated && keycloak.tokenParsed) {
        userProfile.value = keycloak.tokenParsed as KeycloakTokenParsed
        updateRefreshTokenExpiry()
        updateAuthStartedAt()
        // Set up token refresh
        setupTokenRefresh()
      }

      return authenticated
    }
    catch (err) {
      error.value = 'Failed to initialize Keycloak'
      console.error('Keycloak init error:', err)
      throw err
    }
  }

  const login = async (): Promise<void> => {
    try {
      await keycloak.login({
        redirectUri: window.location.origin + window.location.pathname
      })
    }
    catch (err) {
      console.error('Login error:', err)
      throw err
    }
  }

  const logout = async (): Promise<void> => {
    try {
      // Clean up token refresh before logout
      cleanupTokenRefresh()
      isAuthenticated.value = false
      userProfile.value = null
      refreshTokenExpiresAt.value = null

      await keycloak.logout({
        redirectUri: window.location.origin
      })
    }
    catch (err) {
      console.error('Logout error:', err)
      throw err
    }
  }

  const hasRole = (role: string): boolean => {
    return keycloak.hasResourceRole(role)
  }

  const isAdmin = (): boolean => {
    return hasRole('admin')
  }

  const getToken = (): string | undefined => {
    return keycloak.token
  }

  const refreshToken = async (): Promise<boolean> => {
    try {
      return await refreshWithRetry(-1) // Force refresh
    }
    catch (err) {
      console.error('Manual token refresh failed:', err)
      error.value = 'Failed to refresh token'
      // Trigger the terminal flow so the user sees the "Signing you out"
      // modal and gets flushed+redirected instead of being silently stranded
      // with a dead session. Most relevant when "Stay signed in" is clicked
      // near the ssoSessionMaxLifespan ceiling — refresh genuinely cannot
      // succeed there, and the modal would otherwise loop forever.
      await handleTerminalRefreshFailure(err)
      return false
    }
  }

  const registerFlushHandler = (handler: FlushHandler): (() => void) => {
    flushHandlers.add(handler)
    return () => flushHandlers.delete(handler)
  }

  const refreshTokenExpiresInMs = computed(() => {
    if (refreshTokenExpiresAt.value === null) return null
    return refreshTokenExpiresAt.value - Date.now()
  })

  const sessionMaxExpiresAt = computed(() => {
    if (authStartedAt.value === null) return null
    return authStartedAt.value + ssoMaxLifespanMs.value
  })

  return {
    keycloak,
    isAuthenticated: readonly(isAuthenticated),
    isLoggingOut: readonly(isLoggingOut),
    isVerifyingSession: readonly(isVerifyingSession),
    userProfile: readonly(userProfile),
    error: readonly(error),
    isInitialized: readonly(isInitialized),
    refreshTokenExpiresAt: readonly(refreshTokenExpiresAt),
    refreshTokenExpiresInMs,
    sessionMaxExpiresAt,
    ssoMaxLifespanMs: readonly(ssoMaxLifespanMs),
    initKeycloak,
    login,
    logout,
    hasRole,
    isAdmin,
    getToken,
    refreshToken,
    registerFlushHandler,
    flushAll: runFlushHandlers,
    loadSessionTimeoutsFromBackend: fetchSessionTimeouts
  }
}
